import {
  ChangeEventHandler,
  ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
} from 'react';

import Form from 'react-bootstrap/Form';

import { isDefined } from '@consumer-frontend/nc-utils';

export type PhoneInputPropsT = {
  id: string;
  label: ReactNode;
  name: string;
  autoComplete?: string;
  className?: string;
  disabled?: boolean;
  errorMessage?: string;
  helperText?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  optional?: boolean;
  readOnly?: boolean;
  ref?: React.Ref<HTMLInputElement>;
  success?: boolean;
  value?: string | number;
};

const formatPhoneNumber = (digits: string): string => {
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export type PhoneInputT = React.FC<PhoneInputPropsT>;

export const PhoneInput: PhoneInputT = ({
  autoComplete,
  className,
  errorMessage,
  id,
  helperText,
  label,
  optional,
  name,
  onChange,
  success,
  value,
  ...props
}) => {
  const phoneDisplay = formatPhoneNumber(value ? String(value) : '');

  const inputRef = useRef<HTMLInputElement>(null);
  const cursorRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (cursorRef.current === null) return;

    const pos = cursorRef.current;

    inputRef.current?.setSelectionRange(pos, pos);
  }, [phoneDisplay]);

  // Format phone display as (XXX) XXX-XXXX while storing only digits.
  // Track how many digits precede the cursor so we can re-derive the correct
  // cursor position after the formatted string is written back to the DOM.
  const onPhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = e.target.value;
      const selectionStart = e.target.selectionStart ?? rawValue.length;

      const digits = rawValue.replace(/\D/g, '');
      const digitsBeforeCursor = rawValue.slice(0, selectionStart).replace(/\D/g, '').length;
      const formattedPhoneNumber = formatPhoneNumber(digits);

      const digitPositions = Array.from(formattedPhoneNumber).reduce<number[]>(
        (positions, digit, idx) => (/\d/.test(digit) ? [...positions, idx + 1] : positions),
        [],
      );

      cursorRef.current = digitsBeforeCursor === 0
        ? 0
        : (digitPositions[digitsBeforeCursor - 1] ?? formattedPhoneNumber.length);

      if (isDefined(onChange)) {
        onChange({
          target: { name, value: digits },
        } as React.ChangeEvent<HTMLInputElement>);
      }
    },
    [onChange, name],
  );

  const ariaDescribedby = `${helperText ? `${id}-helper` : ''} ${errorMessage ? `${id}-error` : ''}`;

  return (
    <div className={className}>
      <Form.Group controlId={id} className="form-group">
        {label && (
          <Form.Label>
            {label}
            {optional && (
              <span className="optional-label"> (Optional)</span>
            )}
          </Form.Label>
        )}

        {helperText && <p id={`${id}-helper`} className="helper-text">{helperText}</p>}

        <Form.Control
          {...props}
          autoComplete={autoComplete}
          ref={inputRef}
          aria-describedby={ariaDescribedby}
          aria-invalid={!!errorMessage}
          isInvalid={!!errorMessage}
          isValid={success}
          name={name}
          type="tel"
          onChange={onPhoneChange}
          value={phoneDisplay}
        />

        {errorMessage && (
          <Form.Control.Feedback type="invalid" id={`${id}-error`}>
            {errorMessage}
          </Form.Control.Feedback>
        )}
      </Form.Group>
    </div>
  );
};
